import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Pencil, Lock } from "lucide-react";
import { INTAKE_SECTIONS, fieldVisible } from "../lib/intakeSchema";

function formatValue(field, v) {
  if (v === undefined || v === null || v === "") return null;
  if (Array.isArray(v)) return v.length ? v.join(" · ") : null;
  if (field.type === "appliance_multi") {
    const rows = Object.entries(v || {}).filter(([, row]) => row && row.included);
    if (!rows.length) return null;
    return rows
      .map(([key, row]) => {
        const label = (field.options.find((o) => o.key === key) || {}).label || key;
        const bm = [row.brand, row.model].filter(Boolean).join(" · ");
        return bm ? `${label} · ${bm}` : label;
      })
      .join("\n");
  }
  if (field.type === "consent") return v ? "Authorized" : null;
  return String(v);
}

export default function IntakeDetails({ listing, onEdit }) {
  const nav = useNavigate();
  const intake = listing?.intake || {};
  const empty = !listing?.intake_submitted_at && Object.keys(intake).length === 0;

  if (empty) {
    return (
      <div className="rounded-md border border-dashed border-stone-300 bg-white p-10 text-center" data-testid="intake-details-empty">
        <p className="overline text-stone-500 mb-2">Seller Intake</p>
        <h3 className="font-heading text-2xl font-semibold text-stone-900">No intake on file yet.</h3>
        <p className="text-sm text-stone-500 mt-2 max-w-md mx-auto leading-relaxed">
          Capture the 9-section seller intake to anchor every 7-phase deadline and keep MLS, photography, and escrow prep aligned.
        </p>
        <Button
          onClick={() => (onEdit ? onEdit() : nav(`/listing/${listing.id}/intake`))}
          className="mt-5 rounded-full px-5 text-white"
          style={{ backgroundColor: "#010082" }}
          data-testid="intake-start-cta"
        >
          Start intake
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="intake-details">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-stone-500">Seller Intake</p>
          <h3 className="font-heading text-3xl font-semibold tracking-tight text-stone-900 mt-1">
            Everything captured <em className="italic font-normal">from the listing appointment</em>.
          </h3>
          {listing.intake_submitted_at && (
            <p className="text-[11px] text-stone-500 mt-1 uppercase tracking-widest font-semibold">
              Submitted {new Date(listing.intake_submitted_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              {listing.intake_submitted_by ? ` · by ${listing.intake_submitted_by}` : ""}
            </p>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => (onEdit ? onEdit() : nav(`/listing/${listing.id}/intake`))}
          className="rounded-full border-stone-300"
          data-testid="intake-edit"
        >
          <Pencil className="h-3.5 w-3.5 mr-1.5" />
          Edit intake
        </Button>
      </div>

      {INTAKE_SECTIONS.map((section) => {
        const rows = section.fields
          .filter((f) => fieldVisible(f, intake))
          .map((f) => ({ f, val: formatValue(f, intake[f.key]) }))
          .filter((r) => r.val !== null);
        if (!rows.length) return null;
        return (
          <div
            key={section.id}
            className="rounded-md border border-stone-200 bg-white p-6"
            data-testid={`intake-details-${section.id}`}
          >
            <div className="flex items-start gap-6 flex-wrap">
              <div className="shrink-0">
                <span className="section-index text-stone-500 text-xs block">Section {section.index}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-heading text-xl font-semibold tracking-tight text-stone-900">{section.title}</h4>
                {section.privacyNotice && (
                  <p className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
                    <Lock className="h-3 w-3" /> {section.privacyNotice}
                  </p>
                )}
              </div>
            </div>
            <dl className="mt-4 divide-y divide-stone-100">
              {rows.map(({ f, val }) => (
                <div key={f.key} className="py-3 grid grid-cols-1 sm:grid-cols-5 gap-2">
                  <dt className="sm:col-span-2 text-xs uppercase tracking-widest font-semibold text-stone-500 flex items-center gap-1.5">
                    {f.label}
                    {f.sensitive && <Lock className="h-3 w-3 text-amber-600" />}
                  </dt>
                  <dd className="sm:col-span-3 text-sm text-stone-800 whitespace-pre-wrap leading-relaxed">
                    {val}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}
    </div>
  );
}
